<script setup lang="ts">
import { computed, ref } from 'vue'
import { useRouter } from 'vue-router'
import { useQuery } from '@tanstack/vue-query'
import { memberActivitiesApi } from '@/api/member'
import { useSessionStore } from '@/stores/session'
import { usePointerDrag } from '@/composables/usePointerDrag'
import FloatGif from '@/assets/raffle/float.gif'

const DISMISS_STORAGE_KEY = 'lucky_wheel_float_dismissed_until'
const POSITION_STORAGE_KEY = 'lucky_wheel_float_bottom'
const POSITION_X_STORAGE_KEY = 'lucky_wheel_float_left'
const DISMISS_DURATION_MS = 24 * 60 * 60 * 1000
const FLOAT_SIZE = 90
const EDGE_MARGIN = 4

const router = useRouter()
const sessionStore = useSessionStore()
const dismissed = ref(isDismissed())

const { data: activitiesData } = useQuery({
  queryKey: ['member-activities'],
  queryFn: async () => {
    const res = await memberActivitiesApi()
    return res.data
  },
  enabled: () => !!sessionStore.sessionId,
})

const luckyWheel = computed(() => activitiesData.value?.lucky_wheel ?? null)
const visible = computed(() => !!luckyWheel.value && !dismissed.value)

function getDefaultBottom(): number {
  return 80
}

function getMinBottom(): number {
  return getDefaultBottom()
}

function getMaxBottom(): number {
  return Math.max(getMinBottom(), window.innerHeight - FLOAT_SIZE - 16)
}

function clampBottom(value: number): number {
  return Math.min(Math.max(value, getMinBottom()), getMaxBottom())
}

function getInitialBottom(): number {
  const saved = localStorage.getItem(POSITION_STORAGE_KEY)
  if (saved) {
    return clampBottom(Number(saved))
  }
  return getDefaultBottom()
}

function clampLeft(value: number): number {
  return Math.min(
    Math.max(value, EDGE_MARGIN),
    window.innerWidth - FLOAT_SIZE - EDGE_MARGIN,
  )
}

function getInitialLeft(): number {
  const saved = localStorage.getItem(POSITION_X_STORAGE_KEY)
  if (saved) {
    return clampLeft(Number(saved))
  }
  return window.innerWidth - FLOAT_SIZE - EDGE_MARGIN
}

function isDismissed(): boolean {
  const until = localStorage.getItem(DISMISS_STORAGE_KEY)
  if (!until) return false
  return Date.now() < Number(until)
}

const {
  x: leftPx,
  y: bottomPx,
  isDragging,
  suppressClick,
  onPointerDown,
  onPointerMove,
  onPointerUp,
} = usePointerDrag({
  axis: 'both',
  longPressMs: 300,
  moveThreshold: 10,
  initialX: getInitialLeft(),
  initialY: getInitialBottom(),
  clampX: clampLeft,
  clampY: clampBottom,
  invertYDelta: true,
  onDragEnd: ({ x, y }) => {
    localStorage.setItem(POSITION_X_STORAGE_KEY, String(x))
    localStorage.setItem(POSITION_STORAGE_KEY, String(y))
  },
})

function handleClick() {
  if (suppressClick.value || isDragging.value) return
  router.push('/activity/lucky-wheel')
}

function handleDismiss() {
  localStorage.setItem(DISMISS_STORAGE_KEY, String(Date.now() + DISMISS_DURATION_MS))
  dismissed.value = true
}
</script>

<template>
  <div
    v-if="visible"
    class="fixed z-50 select-none touch-none"
    :class="isDragging ? 'cursor-grabbing' : ''"
    :style="{ left: `${leftPx}px`, bottom: `${bottomPx}px` }"
    @pointerdown="onPointerDown"
    @pointermove="onPointerMove"
    @pointerup="onPointerUp"
    @pointercancel="onPointerUp"
  >
    <div class="relative size-[90px]">
      <button
        type="button"
        class="absolute -top-1 -right-1 z-10 flex size-5 items-center justify-center rounded-full bg-black/45 text-white active:opacity-80"
        aria-label="关闭"
        @click.stop="handleDismiss"
        @pointerdown.stop
      >
        <van-icon name="cross" size="16" class="text-white!" />
      </button>

      <button
        type="button"
        class="block size-[90px] transition-transform"
        :class="isDragging ? 'scale-105' : 'active:scale-95'"
        aria-label="幸运大转盘"
        @click="handleClick"
      >
        <img :src="FloatGif" alt="幸运大转盘" class="size-[90px] pointer-events-none" />
      </button>
    </div>
  </div>
</template>
